import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Package, MapPin, Lock, LogOut, Plus, Pencil, Trash2, Check, X, Loader2, ChevronRight, ShoppingBag, FileText, CreditCard, Wifi, Download, ExternalLink, RotateCcw, Truck, Copy } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

import { BACKEND_URL } from '../config/api';
const API = `${BACKEND_URL}/api`;

const AccountPage = () => {
  const navigate = useNavigate();
  const { user, logout, checkAuth, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  
  // Profile state
  const [profile, setProfile] = useState({ name: '', phone: '' });
  const [editingProfile, setEditingProfile] = useState(false);
  
  // Password state
  const [passwords, setPasswords] = useState({ current: '', new: '', confirm: '' });
  
  // Addresses state
  const [addresses, setAddresses] = useState([]);
  const [defaultAddressId, setDefaultAddressId] = useState(null);
  const [editingAddress, setEditingAddress] = useState(null);
  const [newAddress, setNewAddress] = useState(null);
  
  // Orders state
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);

  // Subscriptions state
  const [subscriptions, setSubscriptions] = useState([]);
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(false);
  const [cancellingSubscription, setCancellingSubscription] = useState(null);

  // Invoices state
  const [invoices, setInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);

  useEffect(() => {
    // Wait for auth to finish loading before deciding to redirect
    if (authLoading) return;
    
    if (!user) {
      navigate('/auth', { state: { from: '/account' } });
      return;
    }
    
    setProfile({ name: user.name || '', phone: user.phone || '' });
    fetchAddresses();
    fetchOrders();
  }, [user, authLoading, navigate]);

  // Fetch subscriptions when tab changes
  useEffect(() => {
    if (activeTab === 'subscriptions' && subscriptions.length === 0) {
      fetchSubscriptions();
    }
    if (activeTab === 'invoices' && invoices.length === 0) {
      fetchInvoices();
    }
  }, [activeTab]);

  const fetchAddresses = async () => {
    try {
      const response = await fetch(`${API}/auth/addresses`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setAddresses(data.addresses || []);
        setDefaultAddressId(data.default_address_id);
      }
    } catch (error) {
      console.error('Error fetching addresses:', error);
    }
  };

  const fetchOrders = async () => {
    setLoadingOrders(true);
    try {
      const response = await fetch(`${API}/user/orders`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setOrders(data);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoadingOrders(false);
    }
  };

  const fetchSubscriptions = async () => {
    setLoadingSubscriptions(true);
    try {
      const response = await fetch(`${API}/account/subscriptions`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setSubscriptions(data.subscriptions || []);
      }
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
    } finally {
      setLoadingSubscriptions(false);
    }
  };

  const fetchInvoices = async () => {
    setLoadingInvoices(true);
    try {
      const response = await fetch(`${API}/invoices`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setInvoices(data.invoices || []);
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
    } finally {
      setLoadingInvoices(false);
    }
  };

  const handleCancelSubscription = async (subscriptionId) => {
    if (!window.confirm('Voulez-vous vraiment résilier cet abonnement ?\n\nVous conserverez l\'accès jusqu\'à la fin de la période en cours (sans engagement).')) {
      return;
    }
    
    setCancellingSubscription(subscriptionId);
    try {
      const response = await fetch(`${API}/account/subscriptions/${subscriptionId}/cancel`, {
        method: 'POST',
        credentials: 'include'
      });
      
      const data = await response.json();
      
      if (response.ok) {
        console.log('Success:', data.message);
        fetchSubscriptions();
      } else {
        console.log('Error:', data.detail || 'Erreur lors de la résiliation');
      }
    } catch (error) {
      console.log('Error:', 'Erreur de connexion');
    } finally {
      setCancellingSubscription(null);
    }
  };

  const handleReactivateSubscription = async (subscriptionId) => {
    try {
      const response = await fetch(`${API}/account/subscriptions/${subscriptionId}/reactivate`, {
        method: 'POST',
        credentials: 'include'
      });
      
      const data = await response.json();
      
      if (response.ok) {
        console.log('Success:', data.message);
        fetchSubscriptions();
      } else {
        console.log('Error:', data.detail || 'Erreur lors de la réactivation');
      }
    } catch (error) {
      console.log('Error:', 'Erreur de connexion');
    }
  };

  const handleGenerateInvoicePDF = (orderData) => {
    const legalInfo = {
      companyName: 'VAUDIN GAEL',
      tradeName: 'FGS - ALARME MONTPELLIER',
      siret: '485 272 587 00059',
      tvaNumber: 'FR63485272587',
      rcs: '485 272 587 R.C.S. Montpellier',
      address: 'Bureau 3, 6 B BD Berthelot, 34000 MONTPELLIER',
      phone: '07 45 04 09 29',
      email: 'contact@maprotection-ia.fr'
    };

    const getColorLabel = (color) => {
      const colors = { 'black': 'Noir', 'white': 'Blanc', 'ivory': 'Ivoire', 'grey': 'Gris', 'gray': 'Gris' };
      return colors[color?.toLowerCase()] || color || 'Non spécifié';
    };

    const items = orderData.items || [];
    const promoCode = orderData.promo_code;
    const promoDiscount = orderData.promo_discount || 0;
    const shippingCostTTC = orderData.shipping_cost || 0;
    const subtotalHT = items.reduce((sum, item) => sum + (item.price || 0) * (item.quantity || 1), 0);
    const tvaRate = orderData.has_installation ? 10 : 20;
    
    // Calculate shipping HT and TVA
    const shippingHT = shippingCostTTC / (1 + tvaRate / 100);
    const shippingTVA = shippingCostTTC - shippingHT;
    
    const subtotalAfterPromo = subtotalHT - promoDiscount;
    const productTVA = subtotalAfterPromo * (tvaRate / 100);
    const totalTVA = productTVA + shippingTVA;
    const totalTTC = orderData.total_amount || (subtotalAfterPromo + totalTVA + shippingHT);

    const itemsHtml = items.map(item => `
      <tr>
        <td style="padding: 10px; border-bottom: 1px solid #e5e5e5;">
          <strong>${item.name || 'Produit'}</strong>
          ${item.color ? `<br><span style="color: #666; font-size: 12px;">Couleur: ${getColorLabel(item.color)}</span>` : ''}
        </td>
        <td style="padding: 10px; border-bottom: 1px solid #e5e5e5; text-align: center;">${item.quantity || 1}</td>
        <td style="padding: 10px; border-bottom: 1px solid #e5e5e5; text-align: right;">${(item.price || 0).toFixed(2)} €</td>
        <td style="padding: 10px; border-bottom: 1px solid #e5e5e5; text-align: right; font-weight: bold;">${((item.price || 0) * (item.quantity || 1)).toFixed(2)} €</td>
      </tr>
    `).join('');

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Facture - ${orderData.order_id}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; color: #333; }
          .header { display: flex; justify-content: space-between; margin-bottom: 30px; padding-bottom: 20px; border-bottom: 2px solid #10b981; }
          .logo { font-size: 28px; font-weight: bold; color: #10b981; }
          .logo small { display: block; font-size: 12px; color: #666; font-weight: normal; }
          .company-info { font-size: 11px; color: #666; line-height: 1.5; }
          .invoice-meta { background: #f9f9f9; padding: 15px; border-radius: 8px; margin-bottom: 20px; }
          .invoice-meta table { width: 100%; }
          .invoice-meta td { padding: 4px 10px; font-size: 13px; }
          .invoice-meta .label { color: #666; }
          .invoice-meta .value { font-weight: 500; }
          .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 30px; }
          .info-box { background: #f9f9f9; padding: 15px; border-radius: 8px; }
          .info-box h4 { margin: 0 0 10px 0; color: #333; font-size: 14px; text-transform: uppercase; }
          .info-box p { margin: 5px 0; color: #666; font-size: 14px; }
          table.items { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
          table.items th { background: #10b981; color: white; padding: 12px; text-align: left; }
          .totals { width: 300px; margin-left: auto; }
          .totals tr td { padding: 8px; border-bottom: 1px solid #eee; }
          .totals tr.total { font-weight: bold; font-size: 16px; }
          .totals tr.total td { border-top: 2px solid #10b981; padding-top: 12px; }
          .promo-row { color: #10b981; }
          .legal-footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e5e5; font-size: 10px; color: #666; line-height: 1.6; }
          .legal-footer strong { color: #333; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div>
            <div class="logo">FGS<small>${legalInfo.tradeName}</small></div>
            <div class="company-info" style="margin-top: 10px;">${legalInfo.address}<br>Tél: ${legalInfo.phone} | ${legalInfo.email}</div>
          </div>
          <div style="text-align: right;">
            <div style="font-size: 20px; font-weight: bold; color: #333;">Facture</div>
            <div style="color: #666; font-size: 14px; margin-top: 5px;">
              N° ${orderData.order_id}<br>
              ${new Date(orderData.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })}
            </div>
          </div>
        </div>

        <div class="invoice-meta">
          <table>
            <tr>
              <td class="label">SIRET:</td><td class="value">${legalInfo.siret}</td>
              <td class="label">N° TVA:</td><td class="value">${legalInfo.tvaNumber}</td>
            </tr>
            <tr>
              <td class="label">RCS:</td><td class="value">${legalInfo.rcs}</td>
            </tr>
          </table>
        </div>

        <div class="info-grid">
          <div class="info-box">
            <h4>Client</h4>
            <p><strong>${orderData.shipping_address?.first_name || ''} ${orderData.shipping_address?.last_name || ''}</strong></p>
            <p>${orderData.customer_email || orderData.email || ''}</p>
            <p>${orderData.shipping_address?.phone || ''}</p>
          </div>
          <div class="info-box">
            <h4>Adresse de livraison</h4>
            <p>${orderData.shipping_address?.address_line1 || ''}</p>
            ${orderData.shipping_address?.address_line2 ? `<p>${orderData.shipping_address.address_line2}</p>` : ''}
            <p>${orderData.shipping_address?.postal_code || ''} ${orderData.shipping_address?.city || ''}</p>
          </div>
        </div>

        <table class="items">
          <thead><tr><th>Désignation</th><th style="text-align: center; width: 80px;">Qté</th><th style="text-align: right; width: 100px;">P.U. HT</th><th style="text-align: right; width: 100px;">Total HT</th></tr></thead>
          <tbody>${itemsHtml}</tbody>
        </table>

        <table class="totals">
          <tr><td>Sous-total HT</td><td style="text-align: right;">${subtotalHT.toFixed(2)} €</td></tr>
          ${promoCode ? `<tr class="promo-row"><td>Remise (${promoCode})</td><td style="text-align: right;">-${promoDiscount.toFixed(2)} €</td></tr>` : ''}
          ${shippingCostTTC > 0 ? `<tr><td>Frais de livraison HT</td><td style="text-align: right;">${shippingHT.toFixed(2)} €</td></tr>` : ''}
          <tr><td>TVA (${tvaRate}%)</td><td style="text-align: right;">${totalTVA.toFixed(2)} €</td></tr>
          ${shippingCostTTC === 0 ? `<tr><td>Livraison</td><td style="text-align: right; color: #10b981;">GRATUIT</td></tr>` : ''}
          <tr class="total"><td>Total TTC</td><td style="text-align: right; color: #10b981;">${totalTTC.toFixed(2)} €</td></tr>
        </table>

        <div class="legal-footer">
          <strong>${legalInfo.companyName}</strong> - Entrepreneur individuel<br>
          SIRET: ${legalInfo.siret} | RCS: ${legalInfo.rcs} | TVA: ${legalInfo.tvaNumber}<br>
          ${legalInfo.address} | Tél: ${legalInfo.phone}<br><br>
          <strong>Conditions de paiement:</strong> Paiement comptant à la commande.<br>
          <strong>Garantie:</strong> 4 ans sur tous les produits Ajax Systems conformément à la garantie constructeur.<br>
          En cas de litige, le tribunal compétent sera celui de Montpellier.
        </div>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 250);
  };

  const handleUpdateProfile = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/auth/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(profile)
      });
      
      if (response.ok) {
        console.log('Success:', 'Profil mis à jour');
        setEditingProfile(false);
        checkAuth();
      } else {
        const data = await response.json();
        console.log('Error:', data.detail || 'Erreur lors de la mise à jour');
      }
    } catch (error) {
      console.log('Error:', 'Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwords.new !== passwords.confirm) {
      console.log('Error:', 'Les mots de passe ne correspondent pas');
      return;
    }
    
    if (passwords.new.length < 6) {
      console.log('Error:', 'Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(`${API}/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          current_password: passwords.current,
          new_password: passwords.new
        })
      });
      
      if (response.ok) {
        console.log('Success:', 'Mot de passe modifié');
        setPasswords({ current: '', new: '', confirm: '' });
      } else {
        const data = await response.json();
        console.log('Error:', data.detail || 'Erreur lors du changement');
      }
    } catch (error) {
      console.log('Error:', 'Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAddress = async (address, isNew = false) => {
    setLoading(true);
    try {
      const url = isNew ? `${API}/auth/addresses` : `${API}/auth/addresses/${address.address_id}`;
      const method = isNew ? 'POST' : 'PUT';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(address)
      });
      
      if (response.ok) {
        console.log('Success:', isNew ? 'Adresse ajoutée' : 'Adresse mise à jour');
        fetchAddresses();
        setEditingAddress(null);
        setNewAddress(null);
      } else {
        const data = await response.json();
        console.log('Error:', data.detail || 'Erreur');
      }
    } catch (error) {
      console.log('Error:', 'Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAddress = async (addressId) => {
    if (!window.confirm('Supprimer cette adresse ?')) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API}/auth/addresses/${addressId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        console.log('Success:', 'Adresse supprimée');
        // Mise à jour optimiste de l'état local après confirmation du serveur
        setAddresses(prev => prev.filter(addr => addr.address_id !== addressId));
      } else {
        const data = await response.json();
        console.log('Error:', data.detail || 'Erreur lors de la suppression');
        console.error('Delete address error:', data);
      }
    } catch (error) {
      console.log('Error:', 'Erreur de connexion');
      console.error('Delete address network error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      confirmed: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      shipped: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      delivered: 'bg-green-500/20 text-green-400 border-green-500/30',
      cancelled: 'bg-red-500/20 text-red-400 border-red-500/30'
    };
    const labels = {
      pending: 'En attente',
      confirmed: 'Confirmée',
      shipped: 'Expédiée',
      delivered: 'Livrée',
      cancelled: 'Annulée'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${styles[status] || styles.pending}`}>
        {labels[status] || status}
      </span>
    );
  };

  const tabs = [
    { id: 'profile', label: 'Profil', icon: User },
    { id: 'orders', label: 'Commandes', icon: Package },
    { id: 'subscriptions', label: 'Abonnements', icon: Wifi },
    { id: 'invoices', label: 'Factures', icon: FileText },
    { id: 'addresses', label: 'Adresses', icon: MapPin },
    { id: 'security', label: 'Sécurité', icon: Lock },
  ];

  const emptyAddressForm = {
    label: 'Domicile',
    first_name: '',
    last_name: '',
    address_line1: '',
    address_line2: '',
    city: '',
    postal_code: '',
    country: 'France',
    phone: '',
    is_default: false
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-fgs-primary" />
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="min-h-screen py-8" data-testid="account-page">
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold text-white mb-2">Mon compte</h1>
          <p className="text-fgs-text-muted">Gérez votre profil, vos commandes et vos adresses</p>
        </div>

        <div className="grid lg:grid-cols-4 gap-6">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-fgs-surface border border-fgs-border rounded-xl p-4">
              {/* User Info */}
              <div className="flex items-center gap-3 pb-4 border-b border-fgs-border mb-4">
                {user.picture ? (
                  <img src={user.picture} alt={user.name} className="w-12 h-12 rounded-full" />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-fgs-primary/20 flex items-center justify-center">
                    <User className="w-6 h-6 text-fgs-primary" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-white truncate">{user.name}</p>
                  <p className="text-xs text-fgs-text-muted truncate">{user.email}</p>
                </div>
              </div>

              {/* Navigation */}
              <nav className="space-y-1">
                {tabs.map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                      activeTab === tab.id
                        ? 'bg-fgs-primary text-white'
                        : 'text-fgs-text-muted hover:bg-fgs-surface-highlight hover:text-white'
                    }`}
                  >
                    <tab.icon className="w-4 h-4" />
                    <span className="text-sm font-medium">{tab.label}</span>
                  </button>
                ))}
                
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left text-fgs-accent hover:bg-fgs-accent/10 transition-colors mt-4"
                >
                  <LogOut className="w-4 h-4" />
                  <span className="text-sm font-medium">Déconnexion</span>
                </button>
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            <div className="bg-fgs-surface border border-fgs-border rounded-xl p-6">
              
              {/* Profile Tab */}
              {activeTab === 'profile' && (
                <div>
                  <h2 className="font-heading text-xl font-bold text-white mb-6">Informations personnelles</h2>
                  
                  <div className="space-y-4 max-w-md">
                    <div>
                      <Label className="text-fgs-text-muted text-sm">Email</Label>
                      <Input
                        value={user.email}
                        disabled
                        className="bg-fgs-bg border-fgs-border text-white/50 mt-1"
                      />
                      <p className="text-xs text-fgs-text-muted mt-1">L'email ne peut pas être modifié</p>
                    </div>
                    
                    <div>
                      <Label className="text-fgs-text-muted text-sm">Nom</Label>
                      <Input
                        value={profile.name}
                        onChange={(e) => setProfile(p => ({ ...p, name: e.target.value }))}
                        disabled={!editingProfile}
                        className="bg-fgs-bg border-fgs-border text-white mt-1"
                      />
                    </div>
                    
                    <div>
                      <Label className="text-fgs-text-muted text-sm">Téléphone</Label>
                      <Input
                        value={profile.phone}
                        onChange={(e) => setProfile(p => ({ ...p, phone: e.target.value }))}
                        disabled={!editingProfile}
                        placeholder="06 12 34 56 78"
                        className="bg-fgs-bg border-fgs-border text-white mt-1"
                      />
                    </div>
                    
                    <div className="flex gap-3 pt-4">
                      {editingProfile ? (
                        <>
                          <Button
                            onClick={handleUpdateProfile}
                            disabled={loading}
                            className="bg-fgs-primary hover:bg-fgs-primary-hover"
                          >
                            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                            Enregistrer
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => {
                              setEditingProfile(false);
                              setProfile({ name: user.name || '', phone: user.phone || '' });
                            }}
                            className="border-fgs-border text-white"
                          >
                            Annuler
                          </Button>
                        </>
                      ) : (
                        <Button
                          onClick={() => setEditingProfile(true)}
                          variant="outline"
                          className="border-fgs-border text-white hover:bg-fgs-surface-highlight"
                        >
                          <Pencil className="w-4 h-4 mr-2" />
                          Modifier
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Orders Tab */}
              {activeTab === 'orders' && (
                <div>
                  <h2 className="font-heading text-xl font-bold text-white mb-6">Mes commandes</h2>
                  
                  {loadingOrders ? (
                    <div className="text-center py-12">
                      <Loader2 className="w-8 h-8 animate-spin text-fgs-primary mx-auto" />
                    </div>
                  ) : orders.length === 0 ? (
                    <div className="text-center py-12">
                      <ShoppingBag className="w-16 h-16 text-fgs-text-muted mx-auto mb-4" />
                      <p className="text-fgs-text-muted mb-4">Aucune commande pour le moment</p>
                      <Button
                        onClick={() => navigate('/shop')}
                        className="bg-fgs-primary hover:bg-fgs-primary-hover"
                      >
                        Découvrir la boutique
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {orders.map(order => (
                        <div
                          key={order.order_id}
                          className="bg-fgs-bg border border-fgs-border rounded-lg p-4 hover:border-fgs-primary/50 transition-colors"
                        >
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-3">
                              <span className="text-white font-mono text-sm">{order.order_id}</span>
                              {getStatusBadge(order.status)}
                            </div>
                            <div className="flex items-center gap-2">
                              {order.payment_status === 'paid' && (
                                <Button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    window.open(`${API}/user/orders/${order.order_id}/invoice`, '_blank');
                                  }}
                                  size="sm"
                                  variant="outline"
                                  className="border-fgs-primary/50 text-fgs-primary hover:bg-fgs-primary/10"
                                  data-testid={`download-invoice-${order.order_id}`}
                                >
                                  <Download className="w-4 h-4 mr-1" />
                                  Facture PDF
                                </Button>
                              )}
                              <button
                                onClick={() => navigate(`/account/orders/${order.order_id}`)}
                                className="p-2 rounded-lg hover:bg-fgs-surface transition-colors"
                              >
                                <ChevronRight className="w-5 h-5 text-fgs-text-muted" />
                              </button>
                            </div>
                          </div>
                          
                          {/* Tracking Number */}
                          {order.tracking_number && (
                            <div className="mb-3 p-3 bg-fgs-surface/50 rounded-lg border border-fgs-primary/30">
                              <div className="flex items-center gap-2">
                                <Truck className="w-4 h-4 text-fgs-primary" />
                                <span className="text-fgs-text-muted text-sm">Numéro de suivi :</span>
                                <span className="text-white font-mono text-sm font-medium">{order.tracking_number}</span>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigator.clipboard.writeText(order.tracking_number);
                                    console.log('Success:', 'Numéro de suivi copié !');
                                  }}
                                  className="ml-auto p-1 hover:bg-fgs-primary/20 rounded transition-colors"
                                  title="Copier"
                                >
                                  <Copy className="w-4 h-4 text-fgs-primary" />
                                </button>
                              </div>
                            </div>
                          )}
                          
                          <div className="flex items-center justify-between text-sm">
                            <div className="flex items-center gap-4">
                              <span className="text-fgs-text-muted">
                                {new Date(order.created_at).toLocaleDateString('fr-FR')}
                              </span>
                              <span className="text-fgs-text-muted">
                                {order.items?.length || 0} article{(order.items?.length || 0) > 1 ? 's' : ''}
                              </span>
                            </div>
                            <span className="text-fgs-primary font-mono font-bold">
                              {order.total_amount?.toFixed(2)} €
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Subscriptions Tab */}
              {activeTab === 'subscriptions' && (
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="font-heading text-xl font-bold text-white">Mes abonnements</h2>
                    <Button
                      onClick={fetchSubscriptions}
                      variant="outline"
                      size="sm"
                      className="border-fgs-border text-white hover:bg-fgs-surface"
                    >
                      <RotateCcw className="w-4 h-4 mr-2" />
                      Actualiser
                    </Button>
                  </div>

                  {loadingSubscriptions ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-6 h-6 animate-spin text-fgs-primary" />
                    </div>
                  ) : subscriptions.length === 0 ? (
                    <div className="text-center py-12 bg-fgs-bg rounded-lg border border-fgs-border">
                      <Wifi className="w-12 h-12 text-fgs-text-muted mx-auto mb-4" />
                      <p className="text-fgs-text-muted">Aucun abonnement actif</p>
                      <p className="text-fgs-text-muted text-sm mt-1">Souscrivez un forfait 4G lors de l'achat d'une centrale</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {subscriptions.map(sub => (
                        <div
                          key={sub.subscription_id}
                          className={`bg-fgs-bg border rounded-xl p-5 ${
                            sub.cancel_at_period_end ? 'border-yellow-500/50' :
                            sub.status === 'active' ? 'border-fgs-primary/50' : 'border-fgs-border'
                          }`}
                        >
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex items-center gap-4">
                              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                                sub.status === 'active' ? 'bg-fgs-primary/20' : 'bg-gray-500/20'
                              }`}>
                                <Wifi className={`w-6 h-6 ${
                                  sub.status === 'active' ? 'text-fgs-primary' : 'text-fgs-text-muted'
                                }`} />
                              </div>
                              <div>
                                <h3 className="text-white font-semibold text-lg">{sub.product_name}</h3>
                                <p className="text-fgs-text-muted text-sm">{sub.product_description}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-white font-mono text-xl font-bold">
                                {sub.price.toFixed(2)} €<span className="text-sm text-fgs-text-muted">/{sub.interval === 'month' ? 'mois' : 'an'}</span>
                              </p>
                              <span className={`inline-block mt-1 px-2 py-0.5 rounded text-xs font-medium ${
                                sub.cancel_at_period_end ? 'bg-yellow-500/20 text-yellow-400' :
                                sub.status === 'active' ? 'bg-green-500/20 text-green-400' :
                                sub.status === 'canceled' ? 'bg-red-500/20 text-red-400' :
                                sub.status === 'past_due' ? 'bg-orange-500/20 text-orange-400' :
                                'bg-gray-500/20 text-fgs-text-muted'
                              }`}>
                                {sub.cancel_at_period_end ? 'Résiliation programmée' :
                                 sub.status === 'active' ? 'Actif' :
                                 sub.status === 'canceled' ? 'Résilié' :
                                 sub.status === 'past_due' ? 'Paiement en retard' :
                                 sub.status === 'trialing' ? 'Période d\'essai' : sub.status}
                              </span>
                            </div>
                          </div>

                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 py-4 border-t border-b border-fgs-border">
                            <div>
                              <p className="text-fgs-text-muted text-xs mb-1">Début de période</p>
                              <p className="text-white text-sm">
                                {new Date(sub.current_period_start).toLocaleDateString('fr-FR')}
                              </p>
                            </div>
                            <div>
                              <p className="text-fgs-text-muted text-xs mb-1">Fin de période</p>
                              <p className="text-white text-sm">
                                {new Date(sub.current_period_end).toLocaleDateString('fr-FR')}
                              </p>
                            </div>
                            <div>
                              <p className="text-fgs-text-muted text-xs mb-1">Prochain paiement</p>
                              <p className="text-white text-sm">
                                {sub.cancel_at_period_end ? 'Aucun' : 
                                 sub.upcoming_amount ? `${sub.upcoming_amount.toFixed(2)} €` : '-'}
                              </p>
                            </div>
                            <div>
                              <p className="text-fgs-text-muted text-xs mb-1">Moyen de paiement</p>
                              <p className="text-white text-sm flex items-center gap-1">
                                {sub.payment_method?.last4 ? (
                                  <>
                                    <CreditCard className="w-4 h-4" />
                                    •••• {sub.payment_method.last4}
                                  </>
                                ) : 'Non défini'}
                              </p>
                            </div>
                          </div>

                          {sub.cancel_at_period_end && (
                            <div className="mb-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                              <p className="text-yellow-400 text-sm">
                                Votre abonnement sera résilié le {new Date(sub.current_period_end).toLocaleDateString('fr-FR')}.
                                Vous conservez l'accès jusqu'à cette date.
                              </p>
                            </div>
                          )}

                          <div className="flex items-center gap-3">
                            {sub.status === 'active' && !sub.cancel_at_period_end && (
                              <Button
                                onClick={() => handleCancelSubscription(sub.subscription_id)}
                                disabled={cancellingSubscription === sub.subscription_id}
                                variant="outline"
                                className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                              >
                                {cancellingSubscription === sub.subscription_id ? (
                                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                ) : (
                                  <X className="w-4 h-4 mr-2" />
                                )}
                                Résilier (sans engagement)
                              </Button>
                            )}
                            {sub.cancel_at_period_end && sub.status === 'active' && (
                              <Button
                                onClick={() => handleReactivateSubscription(sub.subscription_id)}
                                className="bg-fgs-primary hover:bg-fgs-primary-hover"
                              >
                                <RotateCcw className="w-4 h-4 mr-2" />
                                Annuler la résiliation
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="mt-6 p-4 bg-fgs-surface rounded-lg border border-fgs-border">
                    <h3 className="text-white font-medium mb-2 flex items-center gap-2">
                      <FileText className="w-5 h-5 text-fgs-primary" />
                      À propos de vos abonnements
                    </h3>
                    <ul className="text-fgs-text-muted text-sm space-y-1">
                      <li>• <strong>Sans engagement</strong> : vous pouvez résilier à tout moment</li>
                      <li>• La résiliation prend effet à la fin de la période en cours</li>
                      <li>• Les factures sont générées automatiquement chaque mois/an</li>
                      <li>• Consultez vos factures dans l'onglet "Factures"</li>
                    </ul>
                  </div>
                </div>
              )}

              {/* Invoices Tab */}
              {activeTab === 'invoices' && (
                <div>
                  <h2 className="font-heading text-xl font-bold text-white mb-6">Mes factures</h2>

                  {loadingInvoices ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-8 h-8 text-fgs-primary animate-spin" />
                    </div>
                  ) : invoices.length === 0 ? (
                    <div className="text-center py-12 bg-fgs-surface rounded-lg border border-fgs-border">
                      <FileText className="w-16 h-16 text-fgs-text-muted mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-white mb-2">Aucune facture</h3>
                      <p className="text-fgs-text-muted">
                        Vos factures apparaîtront ici après votre premier paiement
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {invoices.map((invoice) => (
                        <div
                          key={invoice.id}
                          className="p-5 bg-fgs-surface rounded-xl border border-fgs-border hover:border-fgs-border-hover transition-all"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-4">
                              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                                invoice.status === 'paid' 
                                  ? 'bg-emerald-500/10' 
                                  : invoice.status === 'pending' || invoice.status === 'open'
                                    ? 'bg-yellow-500/10'
                                    : 'bg-red-500/10'
                              }`}>
                                <FileText className={`w-6 h-6 ${
                                  invoice.status === 'paid' 
                                    ? 'text-emerald-400' 
                                    : invoice.status === 'pending' || invoice.status === 'open'
                                      ? 'text-yellow-400'
                                      : 'text-red-400'
                                }`} />
                              </div>
                              <div>
                                <p className="font-medium text-white">
                                  {invoice.description || `Facture ${invoice.number || invoice.id.slice(-8)}`}
                                </p>
                                <p className="text-fgs-text-muted text-sm mt-1">
                                  {invoice.created_at 
                                    ? new Date(invoice.created_at).toLocaleDateString('fr-FR', { 
                                        day: 'numeric', 
                                        month: 'long', 
                                        year: 'numeric' 
                                      })
                                    : 'Date inconnue'
                                  }
                                </p>
                                {invoice.subscription_name && (
                                  <p className="text-fgs-text-muted text-xs mt-1">
                                    <span className="text-fgs-primary">Abonnement:</span> {invoice.subscription_name}
                                  </p>
                                )}
                              </div>
                            </div>

                            <div className="text-right">
                              <p className="text-lg font-mono text-white">
                                {(invoice.amount || 0).toFixed(2)} €
                              </p>
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium mt-2 ${
                                invoice.status === 'paid' 
                                  ? 'bg-emerald-500/10 text-emerald-400' 
                                  : invoice.status === 'pending' || invoice.status === 'open'
                                    ? 'bg-yellow-500/10 text-yellow-400'
                                    : 'bg-red-500/10 text-red-400'
                              }`}>
                                {invoice.status === 'paid' ? 'Payée' : 
                                 invoice.status === 'pending' || invoice.status === 'open' ? 'En attente' : 
                                 invoice.status === 'void' ? 'Annulée' : 'Impayée'}
                              </span>
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-3 mt-4 pt-4 border-t border-fgs-border">
                            {invoice.pdf_url && (
                              <a
                                href={invoice.pdf_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 px-3 py-2 bg-fgs-bg hover:bg-fgs-border rounded-lg text-sm text-white transition-colors"
                              >
                                <Download className="w-4 h-4" />
                                Télécharger PDF
                              </a>
                            )}
                            {invoice.hosted_url && (
                              <a
                                href={invoice.hosted_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 px-3 py-2 bg-fgs-bg hover:bg-fgs-border rounded-lg text-sm text-fgs-text-muted hover:text-white transition-colors"
                              >
                                <ExternalLink className="w-4 h-4" />
                                Voir sur Stripe
                              </a>
                            )}
                            {invoice.order_id && (
                              <button
                                onClick={() => {
                                  setActiveTab('orders');
                                }}
                                className="flex items-center gap-2 px-3 py-2 bg-fgs-bg hover:bg-fgs-border rounded-lg text-sm text-fgs-text-muted hover:text-white transition-colors"
                              >
                                <Package className="w-4 h-4" />
                                Voir commande
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="mt-6 p-4 bg-fgs-surface rounded-lg border border-fgs-border">
                    <h3 className="text-white font-medium mb-2 flex items-center gap-2">
                      <CreditCard className="w-5 h-5 text-fgs-primary" />
                      À propos de vos factures
                    </h3>
                    <ul className="text-fgs-text-muted text-sm space-y-1">
                      <li>• Les factures sont générées automatiquement à chaque paiement</li>
                      <li>• Pour les abonnements, une facture est créée à chaque renouvellement</li>
                      <li>• Vous pouvez télécharger les PDF pour votre comptabilité</li>
                      <li>• Conservez vos factures pendant 10 ans (obligation légale)</li>
                    </ul>
                  </div>
                </div>
              )}

              {/* Addresses Tab */}
              {activeTab === 'addresses' && (
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="font-heading text-xl font-bold text-white">Mes adresses</h2>
                    <Button
                      onClick={() => setNewAddress(emptyAddressForm)}
                      className="bg-fgs-primary hover:bg-fgs-primary-hover"
                      size="sm"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Ajouter
                    </Button>
                  </div>
                  
                  {/* New Address Form */}
                  {newAddress && (
                    <AddressForm
                      address={newAddress}
                      onChange={setNewAddress}
                      onSave={() => handleSaveAddress(newAddress, true)}
                      onCancel={() => setNewAddress(null)}
                      loading={loading}
                      isNew
                    />
                  )}
                  
                  {/* Existing Addresses */}
                  <div className="grid md:grid-cols-2 gap-4">
                    {addresses.map(address => (
                      editingAddress?.address_id === address.address_id ? (
                        <AddressForm
                          key={address.address_id}
                          address={editingAddress}
                          onChange={setEditingAddress}
                          onSave={() => handleSaveAddress(editingAddress)}
                          onCancel={() => setEditingAddress(null)}
                          loading={loading}
                        />
                      ) : (
                        <div
                          key={address.address_id}
                          className={`bg-fgs-bg border rounded-lg p-4 ${
                            address.is_default ? 'border-fgs-primary' : 'border-fgs-border'
                          }`}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-white">{address.label}</span>
                              {address.is_default && (
                                <span className="text-xs bg-fgs-primary/20 text-fgs-primary px-2 py-0.5 rounded">
                                  Par défaut
                                </span>
                              )}
                            </div>
                            <div className="flex gap-1">
                              <button
                                onClick={() => setEditingAddress(address)}
                                className="p-1 text-fgs-text-muted hover:text-white"
                              >
                                <Pencil className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteAddress(address.address_id)}
                                className="p-1 text-fgs-text-muted hover:text-fgs-accent"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                          <p className="text-white text-sm">
                            {address.first_name} {address.last_name}
                          </p>
                          <p className="text-fgs-text-muted text-sm">{address.address_line1}</p>
                          {address.address_line2 && (
                            <p className="text-fgs-text-muted text-sm">{address.address_line2}</p>
                          )}
                          <p className="text-fgs-text-muted text-sm">
                            {address.postal_code} {address.city}
                          </p>
                          {address.phone && (
                            <p className="text-fgs-text-muted text-sm mt-1">{address.phone}</p>
                          )}
                        </div>
                      )
                    ))}
                  </div>
                  
                  {addresses.length === 0 && !newAddress && (
                    <div className="text-center py-12">
                      <MapPin className="w-16 h-16 text-fgs-text-muted mx-auto mb-4" />
                      <p className="text-fgs-text-muted">Aucune adresse enregistrée</p>
                    </div>
                  )}
                </div>
              )}

              {/* Security Tab */}
              {activeTab === 'security' && (
                <div>
                  <h2 className="font-heading text-xl font-bold text-white mb-6">Sécurité</h2>
                  
                  {user.auth_type === 'google' ? (
                    <div className="bg-fgs-bg border border-fgs-border rounded-lg p-4">
                      <p className="text-fgs-text-muted">
                        Vous êtes connecté via Google. Le mot de passe est géré par votre compte Google.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4 max-w-md">
                      <h3 className="font-semibold text-white">Changer le mot de passe</h3>
                      
                      <div>
                        <Label className="text-fgs-text-muted text-sm">Mot de passe actuel</Label>
                        <Input
                          type="password"
                          value={passwords.current}
                          onChange={(e) => setPasswords(p => ({ ...p, current: e.target.value }))}
                          className="bg-fgs-bg border-fgs-border text-white mt-1"
                        />
                      </div>
                      
                      <div>
                        <Label className="text-fgs-text-muted text-sm">Nouveau mot de passe</Label>
                        <Input
                          type="password"
                          value={passwords.new}
                          onChange={(e) => setPasswords(p => ({ ...p, new: e.target.value }))}
                          placeholder="Min. 6 caractères"
                          className="bg-fgs-bg border-fgs-border text-white mt-1"
                        />
                      </div>
                      
                      <div>
                        <Label className="text-fgs-text-muted text-sm">Confirmer le nouveau mot de passe</Label>
                        <Input
                          type="password"
                          value={passwords.confirm}
                          onChange={(e) => setPasswords(p => ({ ...p, confirm: e.target.value }))}
                          className="bg-fgs-bg border-fgs-border text-white mt-1"
                        />
                      </div>
                      
                      <Button
                        onClick={handleChangePassword}
                        disabled={loading || !passwords.current || !passwords.new}
                        className="bg-fgs-primary hover:bg-fgs-primary-hover"
                      >
                        {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                        Changer le mot de passe
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Address Form Component
const AddressForm = ({ address, onChange, onSave, onCancel, loading, isNew }) => {
  return (
    <div className={`bg-fgs-bg border border-fgs-primary rounded-lg p-4 ${isNew ? 'md:col-span-2 mb-4' : ''}`}>
      <h3 className="font-semibold text-white mb-4">
        {isNew ? 'Nouvelle adresse' : 'Modifier l\'adresse'}
      </h3>
      
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-fgs-text-muted text-xs">Label</Label>
          <select
            value={address.label}
            onChange={(e) => onChange({ ...address, label: e.target.value })}
            className="w-full mt-1 px-3 py-2 bg-fgs-surface border border-fgs-border rounded-lg text-white text-sm"
          >
            <option value="Domicile">Domicile</option>
            <option value="Bureau">Bureau</option>
            <option value="Autre">Autre</option>
          </select>
        </div>
        
        <div className="flex items-end">
          <label className="flex items-center gap-2 text-sm text-fgs-text-muted">
            <input
              type="checkbox"
              checked={address.is_default}
              onChange={(e) => onChange({ ...address, is_default: e.target.checked })}
              className="rounded"
            />
            Adresse par défaut
          </label>
        </div>
        
        <div>
          <Label className="text-fgs-text-muted text-xs">Prénom *</Label>
          <Input
            value={address.first_name}
            onChange={(e) => onChange({ ...address, first_name: e.target.value })}
            className="bg-fgs-surface border-fgs-border text-white mt-1 text-sm"
          />
        </div>
        
        <div>
          <Label className="text-fgs-text-muted text-xs">Nom *</Label>
          <Input
            value={address.last_name}
            onChange={(e) => onChange({ ...address, last_name: e.target.value })}
            className="bg-fgs-surface border-fgs-border text-white mt-1 text-sm"
          />
        </div>
        
        <div className="col-span-2">
          <Label className="text-fgs-text-muted text-xs">Adresse *</Label>
          <Input
            value={address.address_line1}
            onChange={(e) => onChange({ ...address, address_line1: e.target.value })}
            placeholder="Numéro et rue"
            className="bg-fgs-surface border-fgs-border text-white mt-1 text-sm"
          />
        </div>
        
        <div className="col-span-2">
          <Label className="text-fgs-text-muted text-xs">Complément</Label>
          <Input
            value={address.address_line2 || ''}
            onChange={(e) => onChange({ ...address, address_line2: e.target.value })}
            placeholder="Bâtiment, étage, etc."
            className="bg-fgs-surface border-fgs-border text-white mt-1 text-sm"
          />
        </div>
        
        <div>
          <Label className="text-fgs-text-muted text-xs">Code postal *</Label>
          <Input
            value={address.postal_code}
            onChange={(e) => onChange({ ...address, postal_code: e.target.value })}
            className="bg-fgs-surface border-fgs-border text-white mt-1 text-sm"
          />
        </div>
        
        <div>
          <Label className="text-fgs-text-muted text-xs">Ville *</Label>
          <Input
            value={address.city}
            onChange={(e) => onChange({ ...address, city: e.target.value })}
            className="bg-fgs-surface border-fgs-border text-white mt-1 text-sm"
          />
        </div>
        
        <div>
          <Label className="text-fgs-text-muted text-xs">Téléphone</Label>
          <Input
            value={address.phone || ''}
            onChange={(e) => onChange({ ...address, phone: e.target.value })}
            className="bg-fgs-surface border-fgs-border text-white mt-1 text-sm"
          />
        </div>
      </div>
      
      <div className="flex gap-2 mt-4">
        <Button
          onClick={onSave}
          disabled={loading}
          size="sm"
          className="bg-fgs-primary hover:bg-fgs-primary-hover"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
          Enregistrer
        </Button>
        <Button
          onClick={onCancel}
          variant="outline"
          size="sm"
          className="border-fgs-border text-white"
        >
          <X className="w-4 h-4 mr-1" />
          Annuler
        </Button>
      </div>
    </div>
  );
};

export default AccountPage;
